import { baseApi } from './baseApi'

import {
  AcceptEraClaimMatchDto,
  BillingArchiveEraClaimDto,
  BillingArchiveEraClaimsDto,
  BillingArchiveEraClaimsResult,
  BillingChargeDto,
  BillingChargeOptumClaimStatusHistoryItem,
  BillingChargesList,
  BillingChargesListQuery,
  BillingEraReportsList,
  BillingEraReportsListQuery,
  BillingEraTransaction,
  BillingTransactionClaim,
  ChargeAttachmentStatus,
  ResolveEraCrossoverPayerDto,
  ChargeClaimStatusInfo,
  ChargeDocument,
  CreateBillingChargeDto,
  EraExceptionQueueList,
  EraExceptionQueueQuery,
  EraImportSummary,
  SubmitBillingChargesResult,
} from 'store/types'

export const billingChargesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createBillingCharge: builder.mutation<BillingChargeDto, CreateBillingChargeDto>({
      query: (body) => ({
        url: `/lab/patients/${body.patientId}/account/charges`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, body) => [
        'BillingCharges',
        'BillingPatientPayments',
        { type: 'Patient', id: Number(body.patientId) },
      ],
    }),
    getBillingCharge: builder.query<BillingChargeDto, { id: string; patientId: string }>({
      query: ({ id, patientId }) => ({ url: `/lab/patients/${patientId}/account/charges/${id}` }),
      providesTags: ['BillingCharges'],
    }),
    getPatientBillingCharges: builder.query<BillingChargeDto[], { patientId: number | string }>({
      query: ({ patientId }) => ({ url: `/lab/patients/${patientId}/account/charges` }),
      providesTags: (result, error, { patientId }) => [
        'BillingPatientPayments',
        { type: 'Patient', id: Number(patientId) },
      ],
    }),
    updateBillingCharge: builder.mutation<BillingChargeDto, Partial<BillingChargeDto>>({
      query: ({ id, patientId, ...data }) => ({
        method: `PUT`,
        url: `/lab/patients/${patientId}/account/charges/${id}`,
        body: { id, patientId, ...data },
      }),
      invalidatesTags: (result, error, body) => [
        'BillingPatientPayments',
        { type: 'Patient', id: Number(body.patientId) },
      ],
    }),
    deleteBillingCharge: builder.mutation<void, { id: string; patientId: string | number }>({
      query: ({ id, patientId }) => ({
        method: 'DELETE',
        url: `/lab/patients/${patientId}/account/charges/${id}`,
      }),
      invalidatesTags: (result, error, body) => [
        'BillingPatientPayments',
        { type: 'Patient', id: Number(body.patientId) },
      ],
    }),
    getBillingCharges: builder.query<BillingChargesList, BillingChargesListQuery>({
      query: ({ type, ...query }) => ({ url: `/lab/billing/charges`, params: query }),
      providesTags: ['BillingCharges'],
    }),
    getBillingEraReports: builder.query<BillingEraReportsList, BillingEraReportsListQuery>({
      query: (query) => ({ url: `/lab/billing/era-reports`, params: query }),
      providesTags: ['BillingEra']
    }),
    getEraTransactions: builder.query<BillingEraTransaction[], { id: string, query: BillingEraReportsListQuery }>({
      query: ({ id, query }) => ({ url: `/lab/billing/era-reports/${id}/transactions`, params: query }),
      providesTags: ['BillingEraTransactions']
    }),
    getTransactionClaims: builder.query<BillingTransactionClaim[], { id: string, transactionId: string, query: BillingEraReportsListQuery }>({
      query: ({ id, transactionId, query }) => ({ url: `/lab/billing/era-reports/${id}/transactions/${transactionId}/claims`, params: query }),
      providesTags: ['BillingTransactionClaims']
    }),
    uploadEraReport: builder.mutation<EraImportSummary, File>({
      query: (file) => {
        const body = new FormData()
        body.append('file', file)

        return { url: `/lab/billing/era-reports/upload`, method: 'POST', body }
      },
      invalidatesTags: ['BillingEra'],
    }),
    // The same upload, one file of a bulk upload: it leaves the ERA list alone,
    // and the caller refreshes it once the whole batch is in rather than after
    // each of up to hundreds of files.
    uploadEraReportInBatch: builder.mutation<EraImportSummary, File>({
      query: (file) => {
        const body = new FormData()
        body.append('file', file)

        return { url: `/lab/billing/era-reports/upload`, method: 'POST', body }
      },
    }),
    submitBillingCharges: builder.mutation<
      SubmitBillingChargesResult,
      {
        ids: string[]
        mocked?: boolean
        claimStatus?: string
        payerRank?: 'primary' | 'secondary' | 'tertiary'
        // A deliberate second submission from the charge page; a batch never sets it.
        resubmit?: boolean
        // Answer with each charge that could not be sent instead of failing on
        // the first one; the charges list's bulk submit asks for it.
        reportFailures?: boolean
      }
    >({
      query: (body) => ({
        url: `/lab/billing/charges/submit`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['BillingPatientPayments'],
    }),
    archiveBillingEraClaim: builder.mutation<void, BillingArchiveEraClaimDto & { id: string }>({
      query: ({ id, ...body }) => ({
        url: `/lab/billing/era-reports/${id}/archive-claim`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { claimId }) => [
        'BillingTransactionClaims',
        'BillingEraTransactions',
        'BillingEra',
        'EraExceptionQueue',
        { type: 'EraPostingPlan', id: claimId },
      ],
    }),
    archiveBillingEraClaims: builder.mutation<BillingArchiveEraClaimsResult, BillingArchiveEraClaimsDto>({
      query: (body) => ({
        url: `/lab/billing/era-reports/archive-claims`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result) => [
        'BillingTransactionClaims',
        'BillingEraTransactions',
        'BillingEra',
        'EraExceptionQueue',
        ...(result?.archived ?? []).map((id) => ({ type: 'EraPostingPlan' as const, id })),
      ],
    }),
    getEraExceptionQueue: builder.query<EraExceptionQueueList, EraExceptionQueueQuery>({
      query: (query) => ({ url: `/lab/billing/era-claims/exceptions`, params: query }),
      providesTags: ['EraExceptionQueue'],
    }),
    acceptEraClaimMatch: builder.mutation<BillingTransactionClaim, AcceptEraClaimMatchDto>({
      query: ({ eraId, claimId, ...body }) => ({
        url: `/lab/billing/era-reports/${eraId}/claims/${claimId}/accept-match`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { claimId }) => [
        'EraExceptionQueue',
        'BillingTransactionClaims',
        { type: 'EraPostingPlan', id: claimId },
      ],
    }),
    // Names the payer a forwarded claim went to: it joins the charge behind
    // the payer that paid, and the id the 835 stated is remembered for next time.
    resolveEraCrossoverPayer: builder.mutation<BillingTransactionClaim, ResolveEraCrossoverPayerDto>({
      query: ({ eraId, claimId, ...body }) => ({
        url: `/lab/billing/era-reports/${eraId}/claims/${claimId}/crossover-payer`,
        method: 'POST',
        body,
      }),
      // The plan is cached per claim: adding a payer to the chain changes who
      // it says the money is posted to, so a stale one would reopen the form
      // on the answer this call just replaced.
      invalidatesTags: (result, error, { claimId }) => [
        'EraExceptionQueue',
        'BillingTransactionClaims',
        'BillingCharges',
        { type: 'EraPostingPlan', id: claimId },
      ],
    }),
    getChargeFileUrl: builder.query<
      { url: string },
      { patientId: string; chargeId: string; key: string }
    >({
      query: ({ patientId, chargeId, key }) => ({
        url: `/lab/patients/${patientId}/account/charges/${chargeId}/files/url`,
        params: { key },
      }),
    }),
    getChargeAttachmentStatuses: builder.query<
      ChargeAttachmentStatus[],
      { patientId: string; chargeId: string }
    >({
      query: ({ patientId, chargeId }) => ({
        url: `/lab/patients/${patientId}/account/charges/${chargeId}/attachments/status`,
      }),
    }),
    getChargeClaimStatus: builder.query<
      ChargeClaimStatusInfo,
      { patientId: string; chargeId: string; mocked?: boolean }
    >({
      query: ({ patientId, chargeId, mocked }) => ({
        url: `/lab/patients/${patientId}/account/charges/${chargeId}/claim-status`,
        params: { mocked },
      }),
    }),
    // Every 277 payer status ever received for the charge's claims, newest first.
    getChargeOptumClaimStatusHistory: builder.query<
      BillingChargeOptumClaimStatusHistoryItem[],
      { patientId: string; chargeId: string }
    >({
      query: ({ patientId, chargeId }) => ({
        url: `/lab/patients/${patientId}/account/charges/${chargeId}/optum-claim-status/history`,
      }),
      providesTags: ['BillingCharges'],
    }),
    // Takes the charge out of the Rejected bucket when its payer rejection
    // will not be fixed by resubmitting.
    resolveChargeOptumClaimStatus: builder.mutation<
      void,
      { patientId: string; chargeId: string }
    >({
      query: ({ patientId, chargeId }) => ({
        url: `/lab/patients/${patientId}/account/charges/${chargeId}/optum-claim-status/resolve`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { patientId }) => [
        'BillingCharges',
        'BillingPatientPayments',
        { type: 'Patient', id: Number(patientId) },
      ],
    }),
    getPatientChargeDocuments: builder.query<ChargeDocument[], { patientId: string }>({
      query: ({ patientId }) => ({
        url: `/lab/patients/${patientId}/account/charges/documents`,
      }),
    }),
    addChargeAttachments: builder.mutation<
      void,
      { patientId: string; chargeId: string; fileKeys: string[]; mocked?: boolean }
    >({
      query: ({ patientId, chargeId, fileKeys, mocked }) => ({
        url: `/lab/patients/${patientId}/account/charges/${chargeId}/attachments`,
        method: 'POST',
        body: { fileKeys, mocked },
      }),
    }),
    retryChargeAttachment: builder.mutation<
      void,
      { patientId: string; chargeId: string; fileKey?: string; mocked?: boolean }
    >({
      query: ({ patientId, chargeId, fileKey, mocked }) => ({
        url: `/lab/patients/${patientId}/account/charges/${chargeId}/attachments/retry`,
        method: 'POST',
        body: { fileKey, mocked },
      }),
    }),
  }),
})

export const {
  useCreateBillingChargeMutation,
  useGetBillingChargeQuery,
  useGetPatientBillingChargesQuery,
  useUpdateBillingChargeMutation,
  useDeleteBillingChargeMutation,
  useGetBillingChargesQuery,
  useLazyGetBillingChargesQuery,
  useGetBillingEraReportsQuery,
  useGetEraTransactionsQuery,
  useGetTransactionClaimsQuery,
  useGetEraExceptionQueueQuery,
  useAcceptEraClaimMatchMutation,
  useResolveEraCrossoverPayerMutation,
  useSubmitBillingChargesMutation,
  useArchiveBillingEraClaimMutation,
  useArchiveBillingEraClaimsMutation,
  useUploadEraReportMutation,
  useUploadEraReportInBatchMutation,
  useLazyGetChargeFileUrlQuery,
  useRetryChargeAttachmentMutation,
  useGetPatientChargeDocumentsQuery,
  useGetChargeOptumClaimStatusHistoryQuery,
  useResolveChargeOptumClaimStatusMutation,
  useAddChargeAttachmentsMutation,
  useGetChargeAttachmentStatusesQuery,
  useGetChargeClaimStatusQuery,
} = billingChargesApi
